import { createSelector } from '@reduxjs/toolkit';
import { List as ImmutableList } from 'immutable';
import _memoize from 'lodash/memoize';

import { authSelectors } from 'app-components/flux/auth';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type RootState from 'app-components/utils/redux-store/rootState';

//  import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';

import { A2A_TRANSFERS_REDUCER_KEY, type A2aTransfer } from './a2aTransfersTypes';
import { pendingTransferTypes, trimDate } from './a2aTransfersUtils';

type State = Pick<RootState, typeof A2A_TRANSFERS_REDUCER_KEY>;

export const getA2aTransfersStore = (state: State) => state[A2A_TRANSFERS_REDUCER_KEY];
export const getResourcesById = (state: State) => getA2aTransfersStore(state).resourcesById;

export const getHasTransfers = (state: State) => Boolean(getResourcesById(state)?.size);

export const getTransferList = createSelector(getResourcesById, (resourcesById) => resourcesById?.toList());

// returns two objects, both of which are lists of grouped transfer collections, grouped by initiation date.
export const getTransferHistory = _memoize((transferList: ImmutableList<A2aTransfer>) => {
  const completedArray: A2aTransfer[] = [];
  const pendingList = transferList.filter((transfer) => {
    if (pendingTransferTypes.includes(transfer?.status || '')) {
      return true;
    }
    completedArray.push(transfer); // if not pending, it's completed
    return false;
  });

  const pendingGroupList = pendingList
    .groupBy((transfer) => trimDate(transfer))
    .sortBy(
      (_group, key) => key || '',
      (a, b) => (a < b ? 1 : -1),
    );

  const completedGroupList = ImmutableList(completedArray)
    .groupBy((transfer) => trimDate(transfer))
    .sortBy(
      (_group, key) => key || '',
      (a, b) => (a < b ? 1 : -1),
    );

  return [pendingGroupList, completedGroupList];
});

export const a2aTransferEnabled = createSelector(
  [
    (state) => authSelectors.isCurrentDatasetShared(state),
    (state) => featureFlagsSelectors.getFeatureFlags(state).get('a2A'),
  ],
  (isShared, featureFlag) => !isShared && featureFlag,
);
